import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import './Modules.scss';
import AlertModal from '../../../components/AlertModal/AlertModal';
import PageTitle from '../../../components/PageTitle/PageTitle';
import Table from '../../../components/Table/Table';
import { DynamicCheckbox } from '../../../components/Inputs/Inputs';

// módulos de conteúdo do widget geridos nesta página. Ao contrário dos outros
// módulos de licenciamento, aqui AUSÊNCIA de registo = visível (ver backend:
// app/services/chat/flow/data.py::_visible_hotel_ids) — por isso o estado
// inicial de cada checkbox, sem nenhuma licença criada, é "marcado".
const CONTENT_MODULES = [
  { code: 'RESTAURANTS', label: 'Restaurantes', countPath: '/restaurants/hotel' },
  { code: 'EVENT_ROOMS', label: 'Salas de Eventos', countPath: '/event-rooms/hotel' },
];

export default function Modules() {
  const { axiosAuth } = useContext(AuthContext);
  const [hotels, setHotels] = useState([]);
  const [groupNameById, setGroupNameById] = useState({});
  const [visibility, setVisibility] = useState({}); // { [hotelId]: { [moduleCode]: boolean } }
  const [counts, setCounts] = useState({}); // { [hotelId]: { [moduleCode]: number|null } }
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null); // `${hotelId}:${moduleCode}`

  const [errorModal, setErrorModal] = useState({ open: false, title: '', message: '' });
  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: '', message: '' });
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: hotelsData }, { data: groupsData }] = await Promise.all([
        axiosAuth.get('/hotels'),
        axiosAuth.get('/groups'),
      ]);

      const nameByGroup = {};
      for (const g of groupsData || []) nameByGroup[g.id] = g.name;
      setGroupNameById(nameByGroup);
      setHotels(hotelsData || []);

      const statusCalls = (hotelsData || []).map((h) =>
        axiosAuth
          .get('/modules/status', { params: { hotel_id: h.id } })
          .then(({ data }) => ({ hotelId: h.id, modules: data?.modules || [] }))
          .catch(() => ({ hotelId: h.id, modules: [] })),
      );
      const results = await Promise.allSettled(statusCalls);

      const nextVisibility = {};
      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        const { hotelId, modules } = r.value;
        const byCode = {};
        for (const m of modules) byCode[m.module_code] = m;
        const entry = {};
        for (const { code } of CONTENT_MODULES) {
          const m = byCode[code];
          // sem registo (source null) ou licenciado explicitamente -> visível
          entry[code] = !m || m.source === null || m.source === undefined || m.licensed === true;
        }
        nextVisibility[hotelId] = entry;
      }
      setVisibility(nextVisibility);

      // nº de restaurantes/salas realmente criados por hotel — o toggle só tem
      // efeito visível no widget se houver pelo menos 1 registo destes
      const countCalls = (hotelsData || []).flatMap((h) =>
        CONTENT_MODULES.map(({ code, countPath }) =>
          axiosAuth
            .get(`${countPath}/${h.id}`)
            .then(({ data }) => ({
              hotelId: h.id,
              code,
              count: Array.isArray(data) ? data.length : 0,
            }))
            .catch(() => ({ hotelId: h.id, code, count: null })),
        ),
      );
      const countResults = await Promise.allSettled(countCalls);
      const nextCounts = {};
      for (const r of countResults) {
        if (r.status !== 'fulfilled') continue;
        const { hotelId, code, count } = r.value;
        nextCounts[hotelId] = { ...(nextCounts[hotelId] || {}), [code]: count };
      }
      setCounts(nextCounts);
    } catch (err) {
      openError('Erro ao carregar módulos', err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, openError]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleToggle = useCallback(
    async (hotelId, moduleCode, checked) => {
      const key = `${hotelId}:${moduleCode}`;
      setVisibility((prev) => ({
        ...prev,
        [hotelId]: { ...prev[hotelId], [moduleCode]: checked },
      }));
      setSavingKey(key);
      try {
        await axiosAuth.post('/modules/licenses', {
          module_code: moduleCode,
          hotel_id: hotelId,
          enabled: checked,
          reason: 'manual',
        });
      } catch (err) {
        setVisibility((prev) => ({
          ...prev,
          [hotelId]: { ...prev[hotelId], [moduleCode]: !checked },
        }));
        openError(
          'Erro ao alterar módulo',
          err?.response?.data?.detail || err?.message,
        );
      } finally {
        setSavingKey(null);
      }
    },
    [axiosAuth, openError],
  );

  const columns = useMemo(
    () => [
      { key: 'name', label: 'Hotel' },
      { key: 'group', label: 'Grupo' },
      ...CONTENT_MODULES.map(({ code, label }) => ({
        key: code,
        label,
        render: (row) => {
          const checked = Boolean(visibility[row.id]?.[code]);
          const disabled = loading || savingKey === `${row.id}:${code}`;
          const count = counts[row.id]?.[code];
          const noData = checked && count === 0;
          return (
            <div className="module-cell">
              <DynamicCheckbox
                name={`${code}-${row.id}`}
                checked={checked}
                disabled={disabled}
                onChange={(_name, isChecked) => handleToggle(row.id, code, isChecked)}
              />
              {count !== undefined && count !== null && (
                <span className={`module-count${noData ? ' warn' : ''}`}>
                  {count === 0 ? 'sem dados criados' : `${count} criado${count === 1 ? '' : 's'}`}
                </span>
              )}
            </div>
          );
        },
      })),
    ],
    [visibility, counts, loading, savingKey, handleToggle],
  );

  const tableData = useMemo(
    () =>
      hotels.map((h) => ({
        id: h.id,
        name: h.name,
        group: groupNameById[h.group_id] ?? '',
      })),
    [hotels, groupNameById],
  );

  return (
    <div className="modules-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || 'Ocorreu um erro'}
          message={errorModal.message}
          onClose={closeError}
        />
      )}

      <PageTitle link="" title="Módulos" />

      <p className="modules-hint">
        Controla se a listagem de Restaurantes e Salas de Eventos aparece no widget de
        chat de cada hotel. Um hotel sem nada configurado aparece marcado (comportamento
        atual mantido) — desmarcar oculta o conteúdo no widget sem apagar os dados.
        "Sem dados criados" a laranja significa que o módulo está ativo mas o hotel ainda
        não tem nenhum restaurante/sala criado em Admin → Restaurantes/Eventos — o botão
        não aparece no widget até lá, independentemente deste toggle.
      </p>

      <div className="container">
        <Table columns={columns} data={tableData} />
      </div>
    </div>
  );
}
